import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Menu, MenuItem, Avatar, Divider, Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon, LocalShipping, AttachMoney, Public,
  Group, Support, AccountCircle, Notifications, Logout, FiberNew, Speed
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', gradient: 'from-purple-500 to-purple-700' },
  { text: 'New Shipments', icon: <FiberNew />, path: '/shipments/new', gradient: 'from-green-500 to-green-700' },
  { text: 'Live Shipments', icon: <Speed />, path: '/shipments/live', gradient: 'from-blue-500 to-blue-700' },
  { text: 'All Shipments', icon: <LocalShipping />, path: '/shipments/all', gradient: 'from-indigo-500 to-indigo-700' },
  { text: 'Billing', icon: <AttachMoney />, path: '/billing', gradient: 'from-yellow-500 to-orange-500' },
  { text: 'Serviceability', icon: <Public />, path: '/serviceability', gradient: 'from-cyan-500 to-cyan-700' },
  { text: 'Team', icon: <Group />, path: '/team', gradient: 'from-pink-500 to-pink-700' },
  { text: 'Support', icon: <Support />, path: '/support', gradient: 'from-teal-500 to-teal-700' }
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'V';
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Vendor AppBar */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Toolbar className="px-6">
          <div className="flex items-center flex-grow">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-xl p-2 backdrop-blur-lg">
                <LocalShipping className="text-white text-3xl" />
              </div>
              <div>
                <Typography variant="h6" className="font-bold text-white">
                  {user?.vendor_name || 'Vendor Portal'}
                </Typography>
                <Typography variant="caption" className="text-white/90">
                  Vendor Dashboard
                </Typography>
              </div>
            </div>
          </div>
          
          <IconButton className="mr-3 text-white">
            <Badge badgeContent={5} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <Typography variant="body2" className="font-semibold text-white">
                {user?.name}
              </Typography>
              <Typography variant="caption" className="text-white/90">
                {user?.role}
              </Typography>
            </div>
            <IconButton onClick={handleMenu} className="p-0">
              <Avatar className="ring-2 ring-white/50 bg-gradient-to-br from-green-500 to-teal-500 font-semibold">
                {getInitials(user?.name)}
              </Avatar>
            </IconButton>
          </div>
          
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
              <AccountCircle className="mr-2" /> My Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout className="mr-2" /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Vendor Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            borderRight: 'none',
            background: 'white',
            boxShadow: '4px 0 24px rgba(0,0,0,0.04)'
          }
        }}
      >
        <Toolbar />
        <div className="overflow-auto pt-6">
          <List className="px-4 space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <div key={item.text} className="relative">
                  <ListItem 
                    button 
                    onClick={() => navigate(item.path)}
                    className={`rounded-2xl transition-all duration-300 ${
                      isActive 
                        ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg transform scale-105` 
                        : 'hover:bg-gray-50 hover:translate-x-1'
                    }`}
                    sx={{
                      mb: 1,
                      py: 1.5,
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon className={`min-w-0 mr-4 ${isActive ? 'text-white' : 'text-gray-600'}`}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        className: `font-medium ${isActive ? 'font-semibold' : ''}`
                      }}
                    />
                  </ListItem>
                  {isActive && (
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-l-full bg-gradient-to-b ${item.gradient}`} />
                  )}
                </div>
              );
            })}
          </List>
        </div>
      </Drawer>

      {/* Main Content */}
      <Box component="main" className="flex-grow p-8">
        <Toolbar />
        <div className="mt-6">
          <Outlet />
        </div>
      </Box>
    </div>
  );
}
